// Lane E — pure structural mapping between the two-zone editor draft and the
// canonical filter-tree IR group shape.
//
// Extracted from `view.ts` so the leaf-scope nesting — the part with the subtle
// round-trip bug where a zone saved with "any" reloads as "all" — is unit-
// testable without standing up the whole ProjectDetail view. These functions are
// pure: no DOM, no `this`, no module side effects. Condition compilation /
// decompilation (which needs the view's token/label helpers) stays in `view.ts`;
// only the structure (and the match it encodes) lives here.
//
// The IR interfaces (`IFilterIRGroup`, `IFilterIRCondition`, `FilterIRMatch`,
// `FilterIRDirection`) are ambient globals declared in `frontend/index.d.ts`.

interface IAssembleZoneInput {
	conditions: Array<IFilterIRCondition>;   // plain (non-"in") compiled conditions
	inGroups: Array<IFilterIRGroup>;         // "is one of" lowered to OR sub-groups
	selections: Array<any>;                  // opaque owner/address-selection leaves
	places: Array<any>;                      // opaque geometry/county leaves
	match: FilterIRMatch;                    // the zone's chosen all/any
	direction: FilterIRDirection;
}

// Assemble one zone's compiled rows into a canonical IR group.
//
// Leaf-scope rule (the reason the top group's match is NOT always the zone's
// match): when a zone has BOTH opaque leaves AND conditions, the leaves are the
// SCOPE — they OR among themselves and AND with the conditions, so a condition
// narrows within the drawn/searched boundary instead of an unbounded OR across
// the whole DB. With zone match "any" the conditions must still OR each other,
// so they go into a nested "any" sub-group AND-ed with the scope under an "all"
// wrapper — meaning the wrapper's match is "all" even though the user picked
// "any". `zoneMatchForGroup` (below) recovers the real match from that sub-group.
export function assembleZoneIRGroup(input: IAssembleZoneInput): IFilterIRGroup | null {
	const {conditions, inGroups, selections, places, direction} = input;
	const hasLeaves = selections.length > 0 || places.length > 0;
	const hasConds = conditions.length > 0 || inGroups.length > 0;
	if (!hasLeaves && !hasConds) {
		return null;
	}
	const zoneMatch: FilterIRMatch = input.match === 'any' ? 'any' : 'all';
	let group: IFilterIRGroup;
	if (hasLeaves && hasConds) {
		const scope: IFilterIRGroup = {kind: 'group', match: 'any', conditions: [], selections, places, groups: []};
		if (zoneMatch === 'any') {
			// Conditions OR among themselves, but the whole set still ANDs with scope.
			const conds: IFilterIRGroup = {kind: 'group', match: 'any', conditions, selections: [], places: [], groups: inGroups};
			group = {kind: 'group', match: 'all', conditions: [], selections: [], places: [], groups: [scope, conds]};
		} else {
			group = {kind: 'group', match: 'all', conditions, selections: [], places: [], groups: [scope, ...inGroups]};
		}
	} else {
		group = {kind: 'group', match: zoneMatch, conditions, selections, places, groups: inGroups};
	}
	if (direction === 'exclude') {
		group.direction = 'exclude';
	}
	return group;
}

// Recover the zone's all/any selector from a loaded IR group.
//
// Normally that is the top group's own match. BUT `assembleZoneIRGroup` encodes a
// "leaves + conditions, match=any" zone as a synthetic AND wrapper
// `{match:'all', groups:[scope, conds]}` where `scope` carries only opaque leaves
// and the `conds` sub-group carries the real conditions with the user's chosen
// match. Reading the wrapper's 'all' (the old bug) would silently flip the user's
// OR back to AND on the next Apply. So: when the top group has no direct
// conditions and no direct leaves, and exactly one nested sub-group carries
// conditions (and no leaves), the zone's match is that sub-group's match.
//
// The `exactly one` guard keeps this from mis-firing on a match=all zone whose
// only conditions are several "is one of" rows (each lowered to its own "any"
// sub-group) — that produces MULTIPLE condition-carriers, so we fall through to
// the top group's match ('all'), which is correct.
export function zoneMatchForGroup(group: IFilterIRGroup | null | undefined): FilterIRMatch {
	if (!group) {
		return 'all';
	}
	const directConds = (group.conditions || []).length > 0;
	const directLeaves = (group.selections || []).length > 0 || (group.places || []).length > 0;
	if (!directConds && !directLeaves) {
		const condCarriers = (group.groups || []).filter(g =>
			((g.conditions || []).length > 0 || (g.groups || []).length > 0)
			&& (g.selections || []).length === 0
			&& (g.places || []).length === 0);
		if (condCarriers.length === 1) {
			return condCarriers[0].match === 'any' ? 'any' : 'all';
		}
	}
	return group.match === 'any' ? 'any' : 'all';
}

// ---- multi-group structure (Stage 1: N include groups + ≤1 exclude group) ----
//
// The IR overloads `group.groups[]` for two different things:
//   (a) the leaf-scope wrapper WITHIN one group — `{match:'all', groups:[scope,
//       conds]}` produced by assembleZoneIRGroup's any-branch, and
//   (b) genuinely-separate include groups combined by the project `filter_match`.
// The distinguisher: the leaf-scope wrapper is always `match:'all'` over exactly
// one leaves-only sub-group + one conditions-only sub-group; multiple include
// groups default to `match:'any'` (union) and each sub-group is a full group.
// `isLeafScopeWrapper` captures (a) so `splitIRToGroups` doesn't mistake a single
// "leaves + conditions" group for two groups.

export interface IProjectFilterGroupsSplit {
	includeGroups: Array<IFilterIRGroup>;
	excludeGroup: IFilterIRGroup | null;
	match: FilterIRMatch;   // how the include groups combine (project filter_match)
}

function isLeafScopeWrapper(group: IFilterIRGroup): boolean {
	if (group.match !== 'all') {
		return false;
	}
	if ((group.conditions || []).length || (group.selections || []).length || (group.places || []).length) {
		return false;
	}
	const subs = group.groups || [];
	if (subs.length !== 2) {
		return false;
	}
	const leavesOnly = subs.filter(g =>
		((g.selections || []).length > 0 || (g.places || []).length > 0)
		&& (g.conditions || []).length === 0 && (g.groups || []).length === 0);
	const condsOnly = subs.filter(g =>
		((g.conditions || []).length > 0 || (g.groups || []).length > 0)
		&& (g.selections || []).length === 0 && (g.places || []).length === 0);
	return leavesOnly.length === 1 && condsOnly.length === 1;
}

// Split a filter-tree IR into the editor's group list. Each returned include
// group is one group-card's worth of (scope leaves + conditions); the exclude
// group (Stage 1: at most one) is the subtract set.
export function splitIRToGroups(ir: IFilterTreeIR | null | undefined): IProjectFilterGroupsSplit {
	const include = ir ? ir.include : null;
	let includeGroups: Array<IFilterIRGroup> = [];
	let match: FilterIRMatch = 'any';
	if (include) {
		const directConds = (include.conditions || []).length > 0;
		const directLeaves = (include.selections || []).length > 0 || (include.places || []).length > 0;
		const subs = include.groups || [];
		if (!directConds && !directLeaves && subs.length >= 1 && !isLeafScopeWrapper(include)) {
			// The include block wraps several distinct groups — each sub-group is a card.
			includeGroups = subs.slice();
			match = include.match === 'all' ? 'all' : 'any';
		} else {
			// One group (a flat condition/leaf group, or the leaf-scope wrapper).
			includeGroups = [include];
		}
	}
	const excludeGroup = ir && ir.exclude ? ir.exclude : null;
	return {includeGroups, excludeGroup, match};
}

// Combine the editor's per-group IR back into one filter-tree IR. `includeGroups`
// are direction-less group structures; a single one becomes the `include` block
// directly, multiple are wrapped under an include block carrying the project
// match. The exclude group (already tagged direction='exclude') is the `exclude`
// block. Returns null when there are no include groups (nothing to build).
export function combineGroupsToIR(
	includeGroups: Array<IFilterIRGroup>,
	excludeGroup: IFilterIRGroup | null,
	match: FilterIRMatch,
): IFilterTreeIR | null {
	if (!includeGroups.length) {
		return null;
	}
	const include: IFilterIRGroup = includeGroups.length === 1 ?
		includeGroups[0] :
		{kind: 'group', match, conditions: [], selections: [], places: [], groups: includeGroups.slice()};
	return {
		ir_version: 1,
		match,
		include,
		exclude: excludeGroup || null,
		provenance: {source: 'ui', unresolved: []},
	};
}
